import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const FILTERS = ["upcoming", "active", "completed", "cancelled", "ended_early", "issues"];

export default function HQBookings() {
  const [filter, setFilter] = useState("completed");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");

  const load = () => {
    api.get(`/hq/bookings?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data || []));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const openBooking = async (bid) => {
    setSelected(bid); setTimeline(null); setNotes([]);
    const r = await api.get(`/hq/bookings/${bid}/timeline`);
    setTimeline(r.data);
    const n = await api.get(`/hq/notes?entity_type=booking&entity_id=${bid}`);
    setNotes(n.data || []);
  };

  const addNote = async () => {
    if (!noteBody.trim() || !selected) return;
    await api.post("/hq/notes", { entity_type: "booking", entity_id: selected, body: noteBody });
    setNoteBody("");
    const n = await api.get(`/hq/notes?entity_type=booking&entity_id=${selected}`);
    setNotes(n.data || []);
  };

  return (
    <div className="p-6 flex gap-6 min-w-0" data-testid="hq-bookings">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="overline">Live bookings</p>
            <h1 className="font-display text-3xl tracking-tighter">Bookings</h1>
          </div>
          <input
            data-testid="hq-bookings-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search id / name…"
            className="bg-transparent border border-cream/15 px-3 py-2 text-cream text-sm focus:border-gold focus:outline-none"
          />
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`hq-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`chip text-[10px] ${filter === f ? "border-gold text-gold bg-gold/5" : ""}`}
            >{f.replace("_", " ")}</button>
          ))}
        </div>
        <div className="border border-cream/10 overflow-x-auto" data-testid="hq-bookings-table">
          <table className="w-full text-xs">
            <thead className="bg-black/40 text-cream/60 uppercase tracking-[0.2em] text-[10px]">
              <tr>
                <th className="p-2 text-left">Booking</th>
                <th className="p-2 text-left">Member</th>
                <th className="p-2 text-left">Studio / Pro</th>
                <th className="p-2 text-left">Service</th>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">State</th>
                <th className="p-2 text-left">Check-in</th>
                <th className="p-2 text-left">Check-out</th>
                <th className="p-2 text-left">Address</th>
                <th className="p-2 text-left">Flags</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={10} className="p-4 text-center text-cream/50">No bookings</td></tr>}
              {items.map((b) => (
                <tr key={b.id} onClick={() => openBooking(b.id)}
                    data-testid={`hq-booking-row-${b.id}`}
                    className={`border-t border-cream/10 hover:bg-cream/[0.03] cursor-pointer ${selected === b.id ? "bg-gold/5" : ""}`}>
                  <td className="p-2 font-mono text-[10px]">{b.id.slice(0, 8)}…</td>
                  <td className="p-2">{b.member_name}</td>
                  <td className="p-2">{b.studio_name}</td>
                  <td className="p-2">{b.service_name}</td>
                  <td className="p-2">{new Date(b.start_time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                  <td className="p-2">
                    <span className={`chip text-[9px] ${
                      b.status === "completed" ? "border-emerald-400/40 text-emerald-300" :
                      b.status === "active" ? "border-gold text-gold" :
                      b.status === "ended_early" ? "border-red-400/40 text-red-300" : ""
                    }`}>{b.status}</span>
                  </td>
                  <td className="p-2 text-cream/70">{b.check_in ? new Date(b.check_in).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}</td>
                  <td className="p-2 text-cream/70">{b.check_out ? new Date(b.check_out).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}</td>
                  <td className="p-2 text-cream/50">{b.check_in ? "released" : "locked"}</td>
                  <td className="p-2 text-red-300 text-[10px]">{(b.report_flags || []).map((f) => f === "safety_incident" ? "SAFETY" : "DISPUTE").join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="w-[420px] shrink-0 border-l border-cream/10 pl-6 min-h-[70vh]" data-testid="hq-booking-drawer">
          <button data-testid="hq-drawer-close" onClick={() => setSelected(null)} className="text-[10px] uppercase tracking-[0.2em] text-cream/50 hover:text-cream">← Close</button>
          {!timeline && <p className="text-cream/50 mt-4">Loading timeline…</p>}
          {timeline && (
            <>
              <p className="overline mt-4">Booking</p>
              <p className="font-display text-2xl tracking-tight">{timeline.booking.studio_name}</p>
              <p className="text-xs text-cream/70">{timeline.booking.service_name} · ${timeline.booking.price} · {timeline.booking.duration_minutes} min</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">Member: {timeline.booking.member_name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Fee: ${timeline.booking.platform_fee} · Payout: ${timeline.booking.professional_payout}{timeline.booking.is_repeat_discount ? " · REPEAT WAIVED" : ""}</p>
              {timeline.studio?.exact_address && (
                <p className="mt-2 text-[10px] text-gold">HQ address: {timeline.studio.exact_address}</p>
              )}

              <p className="overline mt-6">Timeline</p>
              <div className="border border-cream/10 p-3 space-y-2" data-testid="hq-booking-timeline">
                {timeline.events.length === 0 && <p className="text-cream/50 text-xs">No events yet.</p>}
                {timeline.events.map((e, i) => (
                  <div key={i} data-testid={`hq-timeline-event-${i}`} className="flex gap-3">
                    <p className="text-[10px] font-mono text-gold/80 w-24 shrink-0">{new Date(e.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</p>
                    <div className="flex-1">
                      <p className="text-xs text-cream">{e.label}</p>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-cream/40">{e.kind}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="overline mt-6">Internal notes</p>
              <div className="space-y-2" data-testid="hq-notes-list">
                {notes.length === 0 && <p className="text-cream/50 text-xs">No notes yet.</p>}
                {notes.map((n) => (
                  <div key={n.id} className="border border-cream/10 p-2 text-xs">
                    <p className="text-cream/80">{n.body}</p>
                    <p className="text-[9px] text-cream/40 mt-1">{n.author_name} · {n.author_role} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input data-testid="hq-note-input" value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                       placeholder="Internal note (HQ only)"
                       className="flex-1 bg-transparent border border-cream/15 px-2 py-1 text-xs focus:border-gold outline-none" />
                <button data-testid="hq-note-add" onClick={addNote} className="btn-gold px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Add</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
